import { useEffect, useState } from "react";
import {
    DollarSign,
    Edit3,
    Loader2,
    ShieldAlert,
    ShieldCheck,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STAGES = [
    { key: "lapsed", label: "Lapsed" },
    { key: "docs_prepared", label: "Docs prepared" },
    { key: "fees_paid", label: "Fees paid" },
    { key: "filing_submitted", label: "Filing submitted" },
    { key: "under_review", label: "Under DCCA review" },
    { key: "reinstated", label: "Reinstated" },
];

/**
 * Director K's reinstatement + reserve ledger admin tile.
 * Writes to /api/admin/entity-status and /api/admin/reserve/credit.
 */
export default function EntityReinstatementTile() {
    const [data, setData] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState({ status: "", target_reinstatement_date: "", notes: "" });
    const [saving, setSaving] = useState(false);
    const [creditOpen, setCreditOpen] = useState(false);
    const [creditAmount, setCreditAmount] = useState("");
    const [crediting, setCrediting] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/entity-status");
            setData(data);
            setForm({
                status: data.entity.status,
                target_reinstatement_date: data.entity.target_reinstatement_date || "",
                notes: data.entity.notes || "",
            });
        } catch (err) {
            console.error(err);
            setData({ loadError: true });
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (!data) {
        return (
            <div
                className="mt-6 p-6 bg-cream-warm rounded-sm border border-forest/10"
                data-testid="admin-entity-tile-loading"
            >
                <Loader2 size={20} className="animate-spin text-gold" />
            </div>
        );
    }
    if (data.loadError) return null;

    const idx = STAGES.findIndex((s) => s.key === data.entity.status);
    const reinstated = data.entity.status === "reinstated";
    const balance = (data.reserve.reserve_balance_cents || 0) / 100;
    const totalAccrued = (data.reserve.total_accrued_cents || 0) / 100;

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/entity-status", form);
            toast.success("Entity status updated");
            setEditOpen(false);
            await load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not update",
            );
        } finally {
            setSaving(false);
        }
    };

    const credit = async () => {
        const dollars = parseFloat(creditAmount);
        if (!dollars || dollars <= 0) {
            toast.error("Enter a positive dollar amount");
            return;
        }
        setCrediting(true);
        try {
            await api.post("/admin/reserve/credit", {
                net_revenue_cents: Math.round(dollars * 100),
                source: "manual-admin",
                note: `Manual credit by Director`,
            });
            toast.success(`Credited 40% of $${dollars.toFixed(2)} to reserve`);
            setCreditOpen(false);
            setCreditAmount("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Credit failed");
        } finally {
            setCrediting(false);
        }
    };

    return (
        <section
            className="mt-10 p-6 bg-cream-warm rounded-sm border border-forest/10"
            data-testid="admin-entity-reinstatement-tile"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        {reinstated ? (
                            <ShieldCheck size={16} className="text-gold" />
                        ) : (
                            <ShieldAlert size={16} className="text-brown" />
                        )}
                        <span className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                            EIGOO Inc. · Hawai'i DCCA Reinstatement
                        </span>
                    </div>
                    <h2
                        className="font-serif text-3xl text-forest mt-2"
                        data-testid="admin-entity-status-label"
                    >
                        {STAGES[idx]?.label || data.entity.status}
                    </h2>
                    {data.entity.notes && (
                        <p className="text-brown text-sm mt-1 max-w-xl">
                            {data.entity.notes}
                        </p>
                    )}
                    {data.entity.target_reinstatement_date && (
                        <p className="text-brown/70 text-xs mt-1">
                            Target: {data.entity.target_reinstatement_date}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => setCreditOpen(true)}
                        data-testid="admin-reserve-credit-btn"
                        className="btn-pill btn-ghost text-xs"
                    >
                        <DollarSign size={13} /> Credit reserve
                    </button>
                    <button
                        type="button"
                        onClick={() => setEditOpen(true)}
                        data-testid="admin-entity-edit-btn"
                        className="btn-pill btn-primary text-xs"
                    >
                        <Edit3 size={13} /> Update status
                    </button>
                </div>
            </div>

            {/* Progress rail */}
            <ol
                className="mt-6 grid grid-cols-6 gap-2"
                data-testid="admin-entity-progress-rail"
            >
                {STAGES.map((s, i) => {
                    const active = i <= idx;
                    return (
                        <li key={s.key} className="flex flex-col gap-1.5">
                            <span
                                className={`block h-1.5 rounded-sm ${
                                    active ? "bg-gold" : "bg-forest/10"
                                }`}
                                aria-hidden
                            />
                            <span
                                className={`text-[0.55rem] tracking-[0.15em] uppercase leading-tight ${
                                    active ? "text-forest" : "text-forest/40"
                                }`}
                            >
                                {s.label}
                            </span>
                        </li>
                    );
                })}
            </ol>

            {/* Reserve summary */}
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <div className="p-4 bg-cream rounded-sm border border-forest/10">
                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                        Reserve balance
                    </p>
                    <p
                        className="font-serif text-3xl text-forest mt-1"
                        data-testid="admin-reserve-balance"
                    >
                        ${balance.toFixed(2)}
                    </p>
                    <p className="text-brown text-[0.65rem] mt-1">
                        {reinstated ? "Ready to distribute" : "Held pending reinstatement"}
                    </p>
                </div>
                <div className="p-4 bg-cream rounded-sm border border-forest/10">
                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                        Total accrued
                    </p>
                    <p className="font-serif text-3xl text-forest mt-1">
                        ${totalAccrued.toFixed(2)}
                    </p>
                </div>
                <div className="p-4 bg-cream rounded-sm border border-forest/10">
                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                        Ledger entries
                    </p>
                    <p className="font-serif text-3xl text-forest mt-1">
                        {(data.reserve.ledger_entries || []).length}
                    </p>
                </div>
            </div>

            {/* Edit modal */}
            {editOpen && (
                <div
                    className="fixed inset-0 z-50 bg-forest-deep/80 backdrop-blur flex items-center justify-center p-4"
                    role="dialog"
                    data-testid="admin-entity-edit-modal"
                >
                    <div className="bg-cream rounded-sm max-w-md w-full p-6">
                        <div className="flex items-center justify-between">
                            <h3 className="font-serif text-xl text-forest">
                                Update entity status
                            </h3>
                            <button
                                type="button"
                                onClick={() => setEditOpen(false)}
                                className="p-1 hover:text-gold"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="mt-5 space-y-4">
                            <label className="block">
                                <span className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                    Stage
                                </span>
                                <select
                                    value={form.status}
                                    onChange={(e) =>
                                        setForm({ ...form, status: e.target.value })
                                    }
                                    data-testid="admin-entity-status-select"
                                    className="mt-1.5 w-full px-4 py-2.5 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                                >
                                    {STAGES.map((s) => (
                                        <option key={s.key} value={s.key}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                    Target date (YYYY-MM-DD)
                                </span>
                                <input
                                    type="date"
                                    value={form.target_reinstatement_date || ""}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            target_reinstatement_date: e.target.value,
                                        })
                                    }
                                    className="mt-1.5 w-full px-4 py-2.5 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                    Notes
                                </span>
                                <textarea
                                    rows={3}
                                    value={form.notes || ""}
                                    onChange={(e) =>
                                        setForm({ ...form, notes: e.target.value })
                                    }
                                    data-testid="admin-entity-notes"
                                    className="mt-1.5 w-full px-4 py-2.5 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                                />
                            </label>
                        </div>
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            data-testid="admin-entity-save-btn"
                            className="btn-pill btn-gold w-full justify-center mt-6"
                        >
                            {saving ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                "Save"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Credit reserve modal */}
            {creditOpen && (
                <div
                    className="fixed inset-0 z-50 bg-forest-deep/80 backdrop-blur flex items-center justify-center p-4"
                    role="dialog"
                    data-testid="admin-reserve-credit-modal"
                >
                    <div className="bg-cream rounded-sm max-w-md w-full p-6">
                        <div className="flex items-center justify-between">
                            <h3 className="font-serif text-xl text-forest">
                                Credit reserve
                            </h3>
                            <button
                                type="button"
                                onClick={() => setCreditOpen(false)}
                                className="p-1 hover:text-gold"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <p className="mt-3 text-brown text-sm">
                            Enter the <strong>Lane 3 · EIGOO Dropship</strong>{" "}
                            NET revenue amount in dollars. 40% will be
                            credited to EIGOO's reserve (Lane 3 split only —
                            Lanes 1, 2, 4 are not reserve-gated).
                        </p>
                        <label className="block mt-4">
                            <span className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                NET revenue (USD)
                            </span>
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={creditAmount}
                                onChange={(e) => setCreditAmount(e.target.value)}
                                data-testid="admin-reserve-credit-amount"
                                className="mt-1.5 w-full px-4 py-2.5 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                            />
                        </label>
                        <p className="mt-2 text-xs text-brown">
                            EIGOO 40% of ${parseFloat(creditAmount || 0).toFixed(2)} ={" "}
                            <strong className="text-forest">
                                ${(parseFloat(creditAmount || 0) * 0.4).toFixed(2)}
                            </strong>
                        </p>
                        <button
                            type="button"
                            onClick={credit}
                            disabled={crediting}
                            data-testid="admin-reserve-credit-submit"
                            className="btn-pill btn-gold w-full justify-center mt-5"
                        >
                            {crediting ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                "Credit reserve"
                            )}
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
